import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Plus,
} from "lucide-react";
import { format, isSameDay } from "date-fns";
import { de } from "date-fns/locale";
import { z } from "zod";
import { useOrg } from "@/lib/org/OrgContext";
import { useHubConnection } from "@/lib/hub/useHubConnection";
import { ApiError } from "@/api/fetch";
import { calendarQueries, type CalendarEventDto } from "@/lib/calendar/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PageContainer } from "@/components/layout/WorkspaceShell";
import { PageHeader } from "@/components/layout/PageHeader";
import { addDays, formatWeekRange, isToday, startOfDay, startOfWeek, weekDays } from "@/lib/calendar/week";
import { cn } from "@/lib/utils";
import { EventDialog } from "@/components/calendar/EventDialog";

const searchSchema = z.object({
  /** YYYY-MM-DD anchor for the week — defaults to today's week. */
  week: z.string().optional(),
  /** "1" opens the create-event dialog on mount (used by the dashboard quick action). */
  create: z.string().optional(),
});

export const Route = createFileRoute("/o/$slug/calendar/")({
  validateSearch: searchSchema,
  component: CalendarWeekPage,
});

function CalendarWeekPage() {
  const org = useOrg();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();

  // Anchor is just-a-Date inside this week. URL stores YYYY-MM-DD; we
  // re-anchor to Monday for stability of the weekly query window.
  const anchor = useMemo(() => {
    const raw = search.week ? new Date(`${search.week}T00:00:00`) : new Date();
    return Number.isNaN(raw.getTime()) ? new Date() : raw;
  }, [search.week]);
  const monday = useMemo(() => startOfWeek(anchor), [anchor]);
  const days = useMemo(() => weekDays(anchor), [anchor]);

  const fromIso = monday.toISOString();
  const toIso = addDays(monday, 7).toISOString();

  const events = useQuery(calendarQueries.events(org.slug, fromIso, toIso));

  // Live updates: any event create/update/delete in this org invalidates the
  // current week. Cheap; the query is keyed by [from, to] so only this week
  // refetches.
  useHubConnection({
    path: "/hubs/calendar",
    onConnected: (conn) => conn.invoke("SubscribeToOrgAsync", org.id),
    on: {
      EventCreated: () =>
        queryClient.invalidateQueries({ queryKey: ["calendar", "events", org.slug] }),
      EventUpdated: () =>
        queryClient.invalidateQueries({ queryKey: ["calendar", "events", org.slug] }),
      EventDeleted: () =>
        queryClient.invalidateQueries({ queryKey: ["calendar", "events", org.slug] }),
    },
  });

  const goWeek = (offsetDays: number) =>
    void navigate({
      search: (prev) => ({
        ...prev,
        week: format(addDays(monday, offsetDays), "yyyy-MM-dd"),
      }),
      replace: false,
    });
  const goToday = () =>
    void navigate({
      search: (prev) => ({ ...prev, week: undefined }),
      replace: true,
    });

  // Edit dialog state — open with `null` for create, with an event for edit.
  const [dialogState, setDialogState] = useState<
    | { kind: "closed" }
    | { kind: "create"; defaultStart: Date }
    | { kind: "edit"; event: CalendarEventDto }
  >({ kind: "closed" });

  // `?create=1` from the dashboard quick action opens the create dialog on
  // mount, then drops the flag from the URL so a refresh doesn't re-open it.
  useEffect(() => {
    if (search.create) {
      setDialogState({ kind: "create", defaultStart: pickReasonableStart(anchor) });
      void navigate({
        search: (prev) => ({ ...prev, create: undefined }),
        replace: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.create]);

  return (
    <PageContainer>
      <PageHeader
        title="Kalender"
        description="Wochenansicht der Org-Termine. Schichten und Konflikte folgen."
        actions={
          <Button
            onClick={() =>
              setDialogState({ kind: "create", defaultStart: pickReasonableStart(anchor) })
            }
          >
            <Plus className="h-4 w-4" />
            Neuer Termin
          </Button>
        }
      />

      <Toolbar
        rangeLabel={formatWeekRange(anchor)}
        onPrev={() => goWeek(-7)}
        onNext={() => goWeek(7)}
        onToday={goToday}
        onJump={(d) =>
          void navigate({
            search: (prev) => ({ ...prev, week: format(d, "yyyy-MM-dd") }),
            replace: false,
          })
        }
      />

      {events.error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          <AlertTriangle className="mr-2 inline h-4 w-4" />
          {events.error instanceof ApiError && events.error.status === 403
            ? "Keine Berechtigung."
            : `Konnte Termine nicht laden: ${(events.error as Error).message}`}
        </div>
      )}

      <WeekGrid
        days={days}
        events={events.data ?? []}
        loading={events.isLoading}
        onEventClick={(e) => setDialogState({ kind: "edit", event: e })}
        onDayCreate={(d) =>
          setDialogState({
            kind: "create",
            defaultStart: combineDateAndTime(d, 9, 0),
          })
        }
      />

      <EventDialog
        open={dialogState.kind !== "closed"}
        onOpenChange={(open) => {
          if (!open) setDialogState({ kind: "closed" });
        }}
        slug={org.slug}
        mode={
          dialogState.kind === "edit"
            ? { kind: "edit", event: dialogState.event }
            : dialogState.kind === "create"
              ? { kind: "create", defaultStart: dialogState.defaultStart }
              : { kind: "create", defaultStart: new Date() }
        }
      />
    </PageContainer>
  );
}

// ── Toolbar ─────────────────────────────────────────────────────────────────

function Toolbar({
  rangeLabel,
  onPrev,
  onNext,
  onToday,
  onJump,
}: {
  rangeLabel: string;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  onJump: (d: Date) => void;
}) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <Button variant="outline" size="sm" onClick={onPrev} title="Vorherige Woche">
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <Button variant="outline" size="sm" onClick={onToday}>
        Heute
      </Button>
      <Button variant="outline" size="sm" onClick={onNext} title="Nächste Woche">
        <ChevronRight className="h-4 w-4" />
      </Button>
      <span className="ml-2 text-sm font-medium">{rangeLabel}</span>
      <input
        type="date"
        className="ml-auto h-8 rounded-md border border-input bg-transparent px-2 text-sm"
        onChange={(e) => {
          const v = e.target.value;
          if (v) onJump(new Date(`${v}T00:00:00`));
        }}
        title="Springe zu Datum"
      />
    </div>
  );
}

// ── Week grid ───────────────────────────────────────────────────────────────

function WeekGrid({
  days,
  events,
  loading,
  onEventClick,
  onDayCreate,
}: {
  days: Date[];
  events: CalendarEventDto[];
  loading: boolean;
  onEventClick: (e: CalendarEventDto) => void;
  onDayCreate: (day: Date) => void;
}) {
  // Group events into days. An event spanning midnight gets bucketed by its
  // start day; multi-day support is a v2 concern.
  const eventsByDay = useMemo(() => {
    const map = new Map<string, CalendarEventDto[]>();
    for (const e of events) {
      const key = format(new Date(e.startsAt), "yyyy-MM-dd");
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(e);
    }
    for (const list of map.values()) {
      list.sort((a, b) => +new Date(a.startsAt) - +new Date(b.startsAt));
    }
    return map;
  }, [events]);

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
      {days.map((d) => (
        <DayColumn
          key={d.toISOString()}
          day={d}
          events={eventsByDay.get(format(d, "yyyy-MM-dd")) ?? []}
          loading={loading}
          onEventClick={onEventClick}
          onCreate={() => onDayCreate(d)}
        />
      ))}
    </div>
  );
}

function DayColumn({
  day,
  events,
  loading,
  onEventClick,
  onCreate,
}: {
  day: Date;
  events: CalendarEventDto[];
  loading: boolean;
  onEventClick: (e: CalendarEventDto) => void;
  onCreate: () => void;
}) {
  const today = isToday(day);
  return (
    <section
      className={cn(
        "flex min-h-[200px] flex-col rounded-lg border bg-card",
        today && "border-primary/40 ring-1 ring-primary/20",
      )}
    >
      <header className="flex items-center justify-between border-b px-3 py-2">
        <div>
          <div
            className={cn(
              "text-xs uppercase tracking-wide",
              today ? "text-primary" : "text-muted-foreground",
            )}
          >
            {format(day, "EEEE", { locale: de })}
          </div>
          <div className={cn("text-base font-semibold", today && "text-primary")}>
            {format(day, "d. MMM", { locale: de })}
          </div>
        </div>
        <button
          type="button"
          onClick={onCreate}
          className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          title="Termin am Tag anlegen"
        >
          <Plus className="h-4 w-4" />
        </button>
      </header>

      <div className="flex-1 space-y-1 p-2">
        {loading ? (
          <>
            <Skeleton className="h-10" />
            <Skeleton className="h-10" />
          </>
        ) : events.length === 0 ? (
          <p className="px-1 py-3 text-center text-xs text-muted-foreground">Keine Termine.</p>
        ) : (
          events.map((e) => <EventChip key={e.id} event={e} onClick={() => onEventClick(e)} />)
        )}
      </div>
    </section>
  );
}

function EventChip({ event, onClick }: { event: CalendarEventDto; onClick: () => void }) {
  const start = new Date(event.startsAt);
  const end = new Date(event.endsAt);
  const sameDay = isSameDay(start, end);
  return (
    <button
      type="button"
      onClick={onClick}
      className="block w-full rounded-md border bg-background px-2 py-1.5 text-left text-xs transition-colors hover:bg-accent"
    >
      <div className="flex items-center gap-1 text-muted-foreground">
        <CalendarDays className="h-3 w-3 shrink-0" />
        <span className="tabular-nums">
          {format(start, "HH:mm", { locale: de })}
          {!sameDay
            ? ` – ${format(end, "EEE HH:mm", { locale: de })}`
            : ` – ${format(end, "HH:mm", { locale: de })}`}
        </span>
      </div>
      <div className="mt-0.5 line-clamp-2 font-medium">{event.title}</div>
      {event.location && (
        <div className="mt-0.5 flex items-center gap-1 text-muted-foreground">
          <MapPin className="h-3 w-3 shrink-0" />
          <span className="truncate">{event.location}</span>
        </div>
      )}
    </button>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

/** Nine in the morning, on the day-of-week the user is currently viewing. */
function pickReasonableStart(anchor: Date): Date {
  const today = startOfDay(new Date());
  const reference = isToday(anchor) ? today : anchor;
  return combineDateAndTime(reference, 9, 0);
}

function combineDateAndTime(day: Date, hours: number, minutes: number): Date {
  const out = new Date(day);
  out.setHours(hours, minutes, 0, 0);
  return out;
}
